import { useRef, useState, useLayoutEffect } from 'react';
import PaginationControls from '../../shared/PaginationControls';
import Count from './components/Count';
import RankingList from './components/RankingList';
import Toolbar from './components/Toolbar';
import List from './components/Mobile/List';
import Table from './components/DesktopTable/Table';
import ProfileModal from './utils/ProfileModal';
import DeleteModal from './utils/DeleteModal';
import useList from './hooks/List';
import { contactsSortOptions } from '../../utils';

const BONDA_DELETE_WARNING = 'Al eliminar este usuario, el correo quedara asociado a la identificacion actual en la plataforma de Bonda y no se podra crear otro usuario con el mismo correo y diferente identificacion.';

const ContactsList = ({ logout, permission, losesAccess, message }) => {
  const vm = useList({ logout, permission, losesAccess, message });

  const stickyHeaderRef = useRef(null);
  const [stickyOffset, setStickyOffset] = useState(0);

  useLayoutEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setStickyOffset(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white shadow-md rounded-lg" style={{ overflow: 'clip' }}>
      <div className={`px-4 pb-4 grid grid-cols-1 ${vm.canSeeAdmin ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-6`}>
        <Count logout={logout} isSuperAdmin={vm.canSeeAdmin} message={message} />
        {!vm.canSeeAdmin && <RankingList logout={logout} />}
      </div>

      {/* Scroll de página: este bloque (título + filtros) queda fijo con un pequeño espacio
          arriba (sticky top-3, bajo la capa de fondo) mientras el body de la tabla se desplaza. */}
      <div ref={stickyHeaderRef} className="md:sticky md:top-3 md:z-20 md:bg-white">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Usuarios</h2>
        </div>
        <Toolbar
          query={vm.query}
          setQuery={vm.setQuery}
          canSeeAdmin={vm.canSeeAdmin}
          findRole={vm.findRole}
          setFindRole={vm.setFindRole}
          roles={vm.roles}
          isCreator={vm.isCreator}
          isEliminator={vm.isEliminator}
          selectedCount={vm.selected.size}
          onAddUser={vm.addUser}
          onDeleteSelected={() =>
            vm.askDelete(
              Array.from(vm.selected),
              `¿Eliminar ${vm.selected.size} usuario(s) seleccionados? Esta acción no se puede deshacer`
            )
          }
        />
      </div>

      <List
        loading={vm.loading}
        users={vm.users}
        query={vm.query}
        sortConfig={vm.sortConfig}
        sortOptions={contactsSortOptions}
        setSortColumn={vm.setSortColumn}
        toggleSortDirection={vm.toggleSortDirection}
        selected={vm.selected}
        isEliminator={vm.isEliminator}
        isEditor={vm.isEditor}
        toggleOne={vm.toggleOne}
        editUser={vm.editUser}
        askDelete={vm.askDelete}
      />
      <Table
        loading={vm.loading}
        users={vm.users}
        query={vm.query}
        isEliminator={vm.isEliminator}
        isEditor={vm.isEditor}
        selected={vm.selected}
        headerCheckboxRef={vm.headerCheckboxRef}
        allSelected={vm.allSelected}
        toggleAllFiltered={vm.toggleAllFiltered}
        toggleOne={vm.toggleOne}
        sortConfig={vm.sortConfig}
        handleSort={vm.handleSort}
        editUser={vm.editUser}
        askDelete={vm.askDelete}
        stickyOffset={stickyOffset}
      />
      {!vm.loading && (
        <PaginationControls
          page={vm.page}
          total={vm.total}
          pageSize={vm.PAGE_SIZE}
          onPageChange={vm.goToPage}
        />
      )}
      <ProfileModal
        open={vm.open}
        setOpen={vm.setOpen}
        closeActive={vm.closeActive}
        isCreated={vm.isCreated}
        currentUser={vm.currentUser}
        permission={permission}
        logout={logout}
        losesAccess={losesAccess}
        setCloseActive={vm.setCloseActive}
        message={message}
        updateUser={vm.updateUser}
      />
      <DeleteModal
        confirm={vm.confirm}
        closeActive={vm.closeActive}
        onClose={vm.closeDeleteModal}
        onConfirm={vm.handleConfirmDelete}
        warningMessage={BONDA_DELETE_WARNING}
      />
    </div>
  );
};

export default ContactsList;
